import React, { useMemo } from 'react';
import { View, Platform } from 'react-native';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';

// Kept local (and duplicated with ToolCard) to avoid a circular import
// between ToolCard and ToolAvatar. If this grows, hoist it to
// `frontend/constants/tools.js`.
const OUTPUT_TYPE_ICON = {
  text:       'align-left',
  image:      'image',
  video:      'video',
  audio:      'volume',
  music:      'music',
  code:       'code',
  document:   'file-lines',
  '3d-model': 'cube',
};

/**
 * Renders the visual representation of a tool — either the custom uploaded
 * avatar (`tool.avatarPath`) when present, or the configured fallback icon.
 *
 *   • When the tool HAS an avatar: the image renders bare at `size`, with
 *     no background and no border-radius. The avatar IS the chip.
 *   • When the tool has NO avatar: behaviour depends on `backgroundColor`.
 *     If a `backgroundColor` is provided (chip mode), a rounded box of
 *     `backgroundColor` is drawn with the fallback icon centered inside.
 *     Otherwise (inline mode) the bare `<Icon>` is rendered at `size`.
 *
 * Falls back through: `tool.icon` → `OUTPUT_TYPE_ICON[tool.outputType]` →
 * `fallbackIcon` prop → `'wand-magic-sparkles'`.
 */
function ToolAvatar({
  tool,
  size,
  iconSize,
  color,
  backgroundColor,
  borderRadius,
  fallbackIcon = 'wand-magic-sparkles',
  solid = false,
  style,
}) {
  const avatarUri = tool?.avatarPath || null;
  const resolvedIconName = tool?.icon
    || OUTPUT_TYPE_ICON[tool?.outputType]
    || fallbackIcon;
  const resolvedIconSize = iconSize ?? Math.round(size * 0.5);
  const resolvedRadius = borderRadius ?? Math.round(size * 0.25);

  const containerStyle = useMemo(() => ({
    width: size,
    height: size,
    borderRadius: resolvedRadius,
    backgroundColor: backgroundColor,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { flexShrink: 0 } : null),
  }), [size, resolvedRadius, backgroundColor]);

  if (avatarUri) {
    // When the tool has a custom avatar, the avatar itself IS the visual —
    // no bg chip, no rounded clipping. Render the image at the requested
    // size in both modes. Callers still pass their original size (chip
    // width or inline icon size) and that becomes the avatar's box.
    return (
      <Image
        source={{ uri: avatarUri, width: size, height: size }}
        style={[
          {
            width: size,
            height: size,
            ...(Platform.OS === 'web' ? { flexShrink: 0 } : null),
          },
          style,
        ]}
        contentFit="cover"
      />
    );
  }

  if (backgroundColor == null) {
    return <Icon name={resolvedIconName} size={resolvedIconSize} color={color} solid={solid} style={style} />;
  }

  return (
    <View style={[containerStyle, style]}>
      <Icon name={resolvedIconName} size={resolvedIconSize} color={color} solid={solid} />
    </View>
  );
}

export default React.memo(ToolAvatar);
